/*
 * wallpaper-unit-toggle.js — "Ft + In" / "Inches" switch for the three places a
 * shopper enters a wall size:
 *   1. the PDP buy-box card  ("Enter Wall Size",   #bb-wallsize-card)
 *   2. the editor/configurator card ("Enter Your Dimensions", .dimensions-card)
 *   3. the large-file upload form ("Wall Dimensions", /pages/upload-your-own-image)
 *
 * A shopper working from a tape measure in plain inches ("that wall is 137
 * inches") should not have to divide by 12 before they can get a price.
 *
 * How it works — feet + inches stay canonical:
 *   Inches mode does NOT repurpose the existing inches box. It hides the native
 *   ft/in pair and shows an injected "total inches" box that writes straight back
 *   into them (137 -> 11 ft, 5 in) and fires the same input/change events typing
 *   would. So every existing consumer is untouched: the configurator's own
 *   validation and clamps (inches <= 11, feet <= 999, 6" minimum, area cap), the
 *   sqft/price math, wallpaper-buybox.js's validation and its push into the
 *   editor, the saved-state manager, and the cart's Dimensions line-item property
 *   (still `11'5.0" x 8'0.0"`, which the print scripts already parse).
 *
 *   That is also why this file is self-contained — markup, styles and behavior in
 *   one asset. Nothing else in the theme had to change except the script tag that
 *   loads it.
 *
 * The chosen unit is shared by both cards and remembered in localStorage.
 */
(function () {
  if (window.ACUnitToggle) return;

  var STORAGE_KEY = 'ac_wallpaper_units';
  var FTIN = 'ftin';
  var IN = 'in';
  // The feet boxes cap at 100 ft, so inches-only caps at the same wall length.
  var MAX_TOTAL_INCHES = 1200;

  /* Each card: where to hang the toggle, and the input pairs it owns. The class
     names mirror the card's own markup so the injected box looks native. */
  var CARDS = [
    {
      key: 'buybox',
      card: '#bb-wallsize-card',
      mountIn: '.bb-wallsize__head',
      rowSelector: '.bb-wallsize__inputs',
      inputClass: 'bb-wallsize__input',
      fieldClass: 'bb-wallsize__field',
      suffixClass: 'bb-wallsize__suffix',
      invalidClass: 'bb-wallsize__input--invalid',
      rows: [
        { label: 'Width', ft: 'bb-width-feet', in: 'bb-width-inches' },
        { label: 'Height', ft: 'bb-height-feet', in: 'bb-height-inches' }
      ]
    },
    {
      key: 'editor',
      card: '.dimensions-card',
      mountBefore: '.dimensions-inputs',
      mountLabel: 'Units',
      rowSelector: '.dimension-row',
      inputClass: 'dimension-input',
      unitClass: 'dimension-unit',
      rows: [
        { label: 'Width', ft: 'width-feet', in: 'width-inches' },
        { label: 'Height', ft: 'height-feet', in: 'height-inches' }
      ]
    },
    {
      // Same card/row classes as the editor, different input ids — the cards are
      // told apart by their inputs, not by the selector.
      key: 'upload',
      card: '.dimensions-card',
      mountBefore: '.dimensions-inputs',
      mountLabel: 'Units',
      rowSelector: '.dimension-row',
      inputClass: 'dimension-input',
      unitClass: 'dimension-unit',
      rows: [
        { label: 'Width', ft: 'wall-width-feet', in: 'wall-width-inches' },
        { label: 'Height', ft: 'wall-height-feet', in: 'wall-height-inches' }
      ]
    }
  ];

  /* Methods that repaint the ft/in boxes from internal state without firing
     events — wrap them so the inches box follows. [element, method] */
  var HOOKS = [
    ['wallpaper-configurator', 'updateDimensionDisplay'],  // editor restoring saved state
    ['large-file-upload-form', 'reset']                    // upload form clearing after submit
  ];

  var STYLES = [
    '.acu-toggle{display:inline-flex;align-items:center;gap:2px;padding:2px;',
    'border:1px solid #e0e0e0;border-radius:999px;background:#f6f6f6;}',
    '.acu-toggle__btn{-webkit-appearance:none;appearance:none;border:0;margin:0;',
    'padding:5px 12px;border-radius:999px;background:transparent;color:#666;',
    'font-size:12px;font-weight:600;line-height:1.2;white-space:nowrap;cursor:pointer;',
    'transition:background .15s ease,color .15s ease;}',
    '.acu-toggle__btn:hover{color:#1897aa;}',
    '.acu-toggle__btn.is-active{background:#1897aa;color:#fff;}',
    '.acu-toggle__btn:focus-visible{outline:2px solid #1897aa;outline-offset:2px;}',
    '.acu-row{display:flex;align-items:center;justify-content:space-between;',
    'gap:12px;margin-bottom:14px;}',
    '.acu-row__label{font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:#666;}',
    '[data-acu-unit="ftin"] .acu-total-part{display:none !important;}',
    '[data-acu-unit="in"] .acu-native-part{display:none !important;}',
    '[data-acu-unit="in"] .bb-wallsize__input{width:92px;}',
    '[data-acu-unit="in"] .dimension-input{width:88px;}',
    '@media (max-width:480px){.bb-wallsize__head{flex-wrap:wrap;}}'
  ].join('');

  var unit = FTIN;
  var pairs = [];      // { ft, in, total, invalidClass }
  var mirroring = false;

  // ---- helpers ----
  function $(sel, root) { return (root || document).querySelector(sel); }
  function byId(id) { return document.getElementById(id); }
  function num(el) { var v = parseFloat(el && el.value); return isNaN(v) ? 0 : v; }
  function blank(el) { return !el || String(el.value).trim() === ''; }
  function tidy(n) { return Math.round(n * 100) / 100; }  // kill 12ths float dust

  function fire(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function readStoredUnit() {
    // Default to plain inches for first-time visitors; a shopper who has explicitly
    // switched to Ft + In before (persisted here) keeps seeing Ft + In on return.
    try { return localStorage.getItem(STORAGE_KEY) === FTIN ? FTIN : IN; } catch (e) { return IN; }
  }
  function storeUnit(v) {
    try { localStorage.setItem(STORAGE_KEY, v); } catch (e) { /* private mode */ }
  }

  function injectStyles() {
    if (byId('acu-styles')) return;
    var style = document.createElement('style');
    style.id = 'acu-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  // ---- build ----
  function buildToggle() {
    var wrap = document.createElement('div');
    wrap.className = 'acu-toggle';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Measurement units');
    [[IN, 'Inches'], [FTIN, 'Ft + In']].forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'acu-toggle__btn';
      btn.setAttribute('data-acu-value', opt[0]);
      btn.textContent = opt[1];
      wrap.appendChild(btn);
    });
    return wrap;
  }

  // Add the inches-only box to one width/height row, styled like its neighbours.
  function buildTotalBox(cfg, row, rowEl, inEl) {
    var input = document.createElement('input');
    input.type = 'number';
    input.className = cfg.inputClass + ' acu-total-input';
    input.min = 0;
    input.max = MAX_TOTAL_INCHES;
    input.step = 0.1;
    input.setAttribute('inputmode', 'decimal');
    input.placeholder = '0';
    input.setAttribute('aria-label', row.label + ' in inches');

    if (cfg.fieldClass) {
      // Buy box: input + suffix share a positioned wrapper.
      var field = document.createElement('span');
      field.className = cfg.fieldClass + ' acu-total-part';
      var suffix = document.createElement('span');
      suffix.className = cfg.suffixClass;
      suffix.textContent = 'in';
      field.appendChild(input);
      field.appendChild(suffix);
      rowEl.appendChild(field);
    } else {
      // Editor: input and unit label are siblings in the row.
      input.classList.add('acu-total-part');
      var unitEl = document.createElement('span');
      unitEl.className = cfg.unitClass + ' acu-total-part';
      unitEl.textContent = 'in';
      rowEl.appendChild(input);
      rowEl.appendChild(unitEl);
    }
    return input;
  }

  function buildCard(cfg) {
    // Every row must be present before we touch the card.
    var found = cfg.rows.map(function (row) {
      var ftEl = byId(row.ft);
      var inEl = byId(row.in);
      var rowEl = inEl && inEl.closest(cfg.rowSelector);
      return ftEl && inEl && rowEl ? { row: row, ft: ftEl, in: inEl, rowEl: rowEl } : null;
    });
    if (found.some(function (f) { return !f; })) return false;

    // Resolve the card from its own inputs, not by querying the class — the
    // editor and the upload form both use .dimensions-card.
    var card = found[0].ft.closest(cfg.card);
    if (!card || card.hasAttribute('data-acu-unit')) return false;

    found.forEach(function (f) {
      // Tag what was already there so the stylesheet can swap the two views.
      Array.prototype.forEach.call(f.rowEl.children, function (child) {
        child.classList.add('acu-native-part');
      });
      var total = buildTotalBox(cfg, f.row, f.rowEl, f.in);
      pairs.push({ ft: f.ft, in: f.in, total: total, invalidClass: cfg.invalidClass });
    });

    var toggle = buildToggle();
    if (cfg.mountIn && $(cfg.mountIn, card)) {
      $(cfg.mountIn, card).appendChild(toggle);
    } else {
      // Give the toggle its own labelled row above the inputs.
      var line = document.createElement('div');
      line.className = 'acu-row';
      var label = document.createElement('span');
      label.className = 'acu-row__label';
      label.textContent = cfg.mountLabel || 'Units';
      line.appendChild(label);
      line.appendChild(toggle);
      var anchor = cfg.mountBefore && $(cfg.mountBefore, card);
      if (anchor) card.insertBefore(line, anchor);
      else card.appendChild(line);
    }

    card.setAttribute('data-acu-unit', unit);
    return true;
  }

  // ---- value plumbing ----
  // ft/in are the source of truth; the total box is a view of them.
  function mirrorPair(pair) {
    // Don't repaint the box mid-keystroke (e.g. "25." while typing a decimal).
    if (pair.total === document.activeElement && pair.total.validity && pair.total.validity.badInput) return;
    var next;
    if (blank(pair.ft) && blank(pair.in)) {
      next = '';
    } else {
      next = String(tidy(num(pair.ft) * 12 + num(pair.in)));
    }
    // Assigning .value replaces the box's editing text and throws the caret to
    // the front. Chrome sanitizes a half-typed "8." to value "8" with badInput
    // false, so the guard above can't catch it — the echo repaint from our own
    // fire() would eat the decimal point the shopper just typed (and scramble
    // the next digit). Only write when the number actually changed; while the
    // box is focused, numerically-equal also counts as unchanged so editing
    // states like "8." and "8.0" survive.
    var cur = String(pair.total.value);
    var unchanged = cur === next ||
      (pair.total === document.activeElement && cur !== '' && next !== '' &&
        parseFloat(cur) === parseFloat(next));
    if (!unchanged) pair.total.value = next;
    if (pair.invalidClass) {
      var bad = pair.ft.classList.contains(pair.invalidClass) ||
        pair.in.classList.contains(pair.invalidClass);
      pair.total.classList.toggle(pair.invalidClass, bad);
    }
  }

  function mirrorAll() {
    if (mirroring) return;
    mirroring = true;
    pairs.forEach(mirrorPair);
    mirroring = false;
  }

  // Split a typed inches value back into the native pair.
  function pushTotal(pair) {
    // A half-typed decimal like "25." reads as value:"" with badInput:true —
    // leave everything alone until the number is complete, otherwise the
    // mirror wipes the box the shopper is typing in.
    if (pair.total.validity && pair.total.validity.badInput) return;
    var raw = String(pair.total.value).trim();
    mirroring = true;
    if (raw === '') {
      pair.ft.value = '';
      pair.in.value = '';
    } else {
      var total = parseFloat(raw);
      if (isNaN(total) || total < 0) total = 0;
      if (total > MAX_TOTAL_INCHES) {
        total = MAX_TOTAL_INCHES;
        pair.total.value = total;
      }
      var feet = Math.floor(total / 12);
      pair.ft.value = feet;
      pair.in.value = tidy(total - feet * 12);
    }
    mirroring = false;

    fire(pair.ft);
    fire(pair.in);

    // The configurator may have corrected what we just wrote (6" minimum, feet
    // cap). Show the corrected figure rather than the rejected one.
    if (raw !== '') {
      var settled = tidy(num(pair.ft) * 12 + num(pair.in));
      if (settled !== parseFloat(raw)) pair.total.value = settled;
    }
  }

  function setUnit(target) {
    unit = target === IN ? IN : FTIN;
    storeUnit(unit);
    document.querySelectorAll('[data-acu-unit]').forEach(function (card) {
      card.setAttribute('data-acu-unit', unit);
      card.querySelectorAll('.acu-toggle__btn').forEach(function (btn) {
        var on = btn.getAttribute('data-acu-value') === unit;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
    // Nothing to convert — ft/in never stopped being the real value.
    mirrorAll();
  }

  // ---- wiring ----
  function bindPair(pair) {
    ['input', 'change'].forEach(function (evt) {
      pair.total.addEventListener(evt, function () {
        if (mirroring) return;
        pushTotal(pair);
      });
      [pair.ft, pair.in].forEach(function (el) {
        el.addEventListener(evt, function () { if (!mirroring) mirrorPair(pair); });
      });
    });

    // wallpaper-buybox.js flags an empty wall size by adding a class to the ft/in
    // boxes — carry that onto the box the shopper can actually see.
    if (pair.invalidClass && window.MutationObserver) {
      var observer = new MutationObserver(function () { if (!mirroring) mirrorPair(pair); });
      [pair.ft, pair.in].forEach(function (el) {
        observer.observe(el, { attributes: true, attributeFilter: ['class'] });
      });
    }
  }

  /*
   * Some hosts rewrite the ft/in boxes from internal state without firing events
   * (the editor restoring a cart in progress; the upload form clearing itself
   * after a submit), which would leave the inches box showing a stale figure.
   * Wrap those methods so the mirror runs straight after. Falls back to the
   * delayed mirror at the bottom of this file if an element never defines.
   */
  function patchHosts() {
    if (!window.customElements || !customElements.whenDefined) return;
    HOOKS.forEach(function (hook) {
      var tag = hook[0], method = hook[1];
      customElements.whenDefined(tag).then(function (Ctor) {
        var C = Ctor || customElements.get(tag);
        var proto = C && C.prototype;
        var flag = '__acuPatched_' + method;
        if (!proto || typeof proto[method] !== 'function' || proto[flag]) return;
        proto[flag] = true;
        var original = proto[method];
        proto[method] = function () {
          var result = original.apply(this, arguments);
          try { mirrorAll(); } catch (e) { /* never break the host over a mirror */ }
          return result;
        };
      })['catch'](function () { /* noop */ });
    });
  }

  function onToggleClick(e) {
    var btn = e.target && e.target.closest && e.target.closest('.acu-toggle__btn');
    if (!btn) return;
    e.preventDefault();
    var target = btn.getAttribute('data-acu-value') === IN ? IN : FTIN;
    if (target !== unit) setUnit(target);
  }

  function init() {
    var built = false;
    CARDS.forEach(function (cfg) { if (buildCard(cfg)) built = true; });
    if (!built) return;

    injectStyles();
    pairs.forEach(function (pair) { if (!pair.bound) { pair.bound = true; bindPair(pair); } });

    if (!window.__acuBound) {
      window.__acuBound = true;
      document.addEventListener('click', onToggleClick);
      patchHosts();
    }
    setUnit(readStoredUnit());
  }

  window.ACUnitToggle = {
    get: function () { return unit; },
    set: setUnit,
    sync: mirrorAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The buy box and the editor can render late on some templates; re-run cheaply.
  setTimeout(init, 600);
  setTimeout(mirrorAll, 1500);
})();
